
import type { TermSuccessDataPoint } from '@empathybroker/tsclient-stats/dist/models/termsuccessreport/termsuccessdata-point'
import type { PropType } from 'vue'
import type { KeywordData } from '../../../models/views/keywords'

import { defineComponent } from 'vue'

import { KpiEnum } from '../../../enums'

import { UiProgressLinear } from '../../keen-ui'
import KpiArcChart from '../../kpi-arc-chart.component.vue'
import KpiBar from '../../kpi-bar.component.vue'
import { EpbButton } from '../../shared/epb-button'
import TransitionSize from '../../transition-size.component.vue'

export default defineComponent({
  name: 'KeywordDetails',
  components: {
    EpbButton,
    KpiArcChart,
    KpiBar,
    TransitionSize,
    UiProgressLinear,
  },
  props: {
    data: {
      type: Object as PropType<KeywordData>,
      required: true,
    },
    messages: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      baseClass: 'epb-keyword-details',
      expandedQueriesIds: [],
      detailsKpis: [KpiEnum.FINDABILITY, KpiEnum.ADD2CART, KpiEnum.NO_RESULTS],
      KpiEnum,
    }
  },
  methods: {
    getQueryCountText(count: number): string {
      return this.$epb.utils.formatNumber(count, { maximumFractionDigits: 2 }, true)
    },
    getKpiData(kpi: KpiEnum, queryData: TermSuccessDataPoint) {
      const { valueLabel, percentageLabel } = this.getKpiMessages(kpi)
      const { count: kpiCount, pct: kpiPct } = this.getKpiValueIds(kpi)
      const percentageFn: Record<string, () => number> = {
        [KpiEnum.QUERIES]: () => (queryData[kpiCount] / this.data.overall.queryCount) * 100,
        [KpiEnum.NO_RESULTS]: () =>
          (queryData[kpiCount] / queryData[this.getKpiValueIds(KpiEnum.QUERIES).count]) * 100,
        default: () => queryData[kpiPct],
      }
      return {
        id: kpi,
        label: percentageLabel || valueLabel,
        valueLabel,
        percentage:
          Math.round((percentageFn[kpi] ? percentageFn[kpi]() : percentageFn.default()) * 100) /
          100,
        count: queryData[kpiCount],
      }
    },
    getKpiValueIds(kpi: KpiEnum): { pct?: string; count?: string } {
      const mapping = {
        [KpiEnum.QUERIES]: { count: 'queryCount' },
        [KpiEnum.FINDABILITY]: { pct: 'successCountPct', count: 'successCount' },
        [KpiEnum.ADD2CART]: { pct: 'add2cartCountPct', count: 'add2cartCount' },
        [KpiEnum.NO_RESULTS]: { pct: 'noResultsCountPct', count: 'noResultsCount' },
      }

      return mapping[kpi] || {}
    },
    getKpiMessages(kpi: KpiEnum) {
      const kpisMessages = this.$epb.messages.insights.analytics.kpis
      return kpisMessages[kpi === KpiEnum.NO_RESULTS ? 'noResults' : kpi]
    },
    getExpandQueryButtonClasses(queryId: string): Array<string | Record<string, boolean>> {
      return [
        `${this.baseClass}__button`,
        `${this.baseClass}__-button--expand-query`,
        { [`${this.baseClass}__button--query-expanded`]: this.isQueryExpanded(queryId) },
      ]
    },
    isQueryExpanded(queryId: string): boolean {
      return this.expandedQueriesIds.includes(queryId)
    },
    onExpandQuery(queryId: string): void {
      const indexOfExpandedQueryId = this.expandedQueriesIds.indexOf(queryId)
      if (indexOfExpandedQueryId < 0) {
        this.expandedQueriesIds.push(queryId)
      } else {
        this.expandedQueriesIds.splice(indexOfExpandedQueryId, 1)
      }
    },
  },
})
